import { getSeoDefaults, localeTag } from "@plaatplan/i18n";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "./siteConfig";

/**
 * @param {string | undefined} [locale]
 */
export function buildWebSiteSchema(locale) {
  return {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    inLanguage: localeTag(locale),
  };
}

/**
 * @param {string | undefined} [locale]
 */
export function buildSoftwareApplicationSchema(locale) {
  const seo = getSeoDefaults(locale);

  return {
    "@type": "WebApplication",
    "@id": `${SITE_URL}/#app`,
    name: SITE_NAME,
    url: SITE_URL,
    applicationCategory: "DesignApplication",
    operatingSystem: "Web",
    browserRequirements: "Requires JavaScript",
    inLanguage: seo.inLanguage,
    description: seo.defaultDescription,
    image: DEFAULT_OG_IMAGE,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
    },
    featureList: seo.features,
    isPartOf: { "@id": `${SITE_URL}/#website` },
  };
}

/**
 * @param {{ title: string, description: string, url: string, locale?: string }} page
 */
export function buildWebPageSchema({ title, description, url, locale }) {
  return {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: localeTag(locale),
    isPartOf: { "@id": `${SITE_URL}/#website` },
  };
}

/**
 * @param {{ name: string, path: string }[]} items
 */
export function buildBreadcrumbListSchema(items) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export function buildFaqPageSchema(items) {
  const mainEntity = items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  }));

  return {
    "@type": "FAQPage",
    mainEntity,
  };
}

/**
 * @param {{ name: string, description: string, url: string, steps: { name: string, text: string }[], locale?: string }} howTo
 */
export function buildHowToSchema({ name, description, url, steps, locale }) {
  return {
    "@type": "HowTo",
    "@id": `${url}#howto`,
    name,
    description,
    inLanguage: localeTag(locale),
    step: steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
      url: `${url}#stap-${index + 1}`,
    })),
  };
}

/**
 * @param {{ locale?: string, title: string, description: string, url: string, faq?: { question: string, answer: string }[], breadcrumb?: { name: string, path: string }[], howTo?: { name: string, description: string, steps: { name: string, text: string }[] } }} options
 */
export function buildStructuredDataGraph({
  locale,
  title,
  description,
  url,
  faq,
  breadcrumb,
  howTo,
}) {
  const graph = [
    buildWebSiteSchema(locale),
    buildSoftwareApplicationSchema(locale),
    buildWebPageSchema({ title, description, url, locale }),
  ];

  if (breadcrumb?.length) {
    graph.push(buildBreadcrumbListSchema(breadcrumb));
  }

  if (howTo?.steps?.length) {
    graph.push(buildHowToSchema({ ...howTo, url, locale }));
  }

  if (faq?.length) {
    graph.push(buildFaqPageSchema(faq));
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
